import { useState } from "react";
import {
  Mail,
  MessageSquare,
  Send,
  GitBranch,
  Reply,
  Phone,
  Inbox,
  Sparkles,
  Clock,
  CheckCircle2,
  XCircle,
  BookOpen,
  Zap,
  ExternalLink,
  Quote,
} from "lucide-react";

/* --- Data --------------------------------------------------------- */

const TOP_STATS = [
  {
    id: "stat-emails",
    label: "Emails Sent This Month",
    value: "1,247",
    Icon: Mail,
    accent: "#F59E0B",
    subs: [{ k: "Open rate", v: "34%", c: "#F59E0B" }],
  },
  {
    id: "stat-sms",
    label: "SMS Sent This Month",
    value: "612",
    Icon: MessageSquare,
    accent: "#0EA5E9",
    subs: [
      { k: "Open rate", v: "98%", c: "#00C2A8" },
      { k: "Click rate", v: "41%", c: "#0EA5E9" },
    ],
    highlight: "HIGHEST-LEVERAGE",
  },
  {
    id: "stat-sequences",
    label: "Sequences Active",
    value: "3",
    Icon: GitBranch,
    accent: "#00C2A8",
    subs: [{ k: "Fast-Track / 7-Day / 14-Day", v: "", c: "#7A9BB5" }],
  },
  {
    id: "stat-replies",
    label: "Replies Captured",
    value: "89",
    Icon: Reply,
    accent: "#00C2A8",
    subs: [{ k: "Auto-routed to rep or booking", v: "", c: "#7A9BB5" }],
  },
];

const SEQUENCE_ROWS = [
  {
    id: "sr-1",
    name: "Dr. Sarah Chen",
    company: "Midwest Health",
    sequence: "Fast-Track",
    seqTone: "#00C2A8",
    day: "Day 3",
    next: "Email in 2h",
    channel: "Email",
    engagement: "Opened Day 2 SMS",
    engTone: "green",
  },
  {
    id: "sr-2",
    name: "Dr. Marcus Webb",
    company: "Regional Surgical",
    sequence: "7-Day",
    seqTone: "#0EA5E9",
    day: "Day 5",
    next: "SMS in 45m",
    channel: "SMS",
    engagement: "Replied Day 3",
    engTone: "green",
  },
  {
    id: "sr-3",
    name: "Amanda Torres",
    company: "Valley Medical",
    sequence: "14-Day",
    seqTone: "#F59E0B",
    day: "Day 4",
    next: "Email in 6h",
    channel: "Email",
    engagement: "Opened Day 1",
    engTone: "teal",
  },
  {
    id: "sr-4",
    name: "Dr. Priya Nair",
    company: "Pacific Surgical",
    sequence: "7-Day",
    seqTone: "#0EA5E9",
    day: "Day 2",
    next: "Voice in 1h",
    channel: "Voice",
    engagement: "Clicked email link",
    engTone: "teal",
  },
  {
    id: "sr-5",
    name: "Robert Kim",
    company: "Great Lakes",
    sequence: "14-Day",
    seqTone: "#F59E0B",
    day: "Day 6",
    next: "Email in 3h",
    channel: "Email",
    engagement: "No engagement (watch)",
    engTone: "amber",
  },
  {
    id: "sr-6",
    name: "Dr. Lisa Tran",
    company: "Summit Ortho",
    sequence: "Fast-Track",
    seqTone: "#00C2A8",
    day: "Day 1",
    next: "COMPLETE",
    channel: "—",
    engagement: "CONVERTED ✓",
    engTone: "green",
    complete: true,
  },
];

const PREVIEWS = {
  "ft-day1": {
    id: "ft-day1",
    label: "Fast-Track Day 1",
    title: "Day 1 Voice-Call Fallback Email",
    accent: "#00C2A8",
    channel: "Email",
    Icon: Mail,
    from: "Alex (rep's real inbox via n8n)",
    subject: "Missed you today, [Name] — quick note",
    body: `Hi [Name],

Just tried to reach you by phone about MedTech Summit — no worries if now isn't the right time.

The short version: we're helping surgical teams cut device setup complexity by 20%+. If that's on your radar for Q3, worth a 15-min call?

I'll try you once more tomorrow. In the meantime, here's the 90-second overview.

Alex`,
    cta: "See 90-sec overview →",
  },
  "day2-sms": {
    id: "day2-sms",
    label: "Day 2 SMS",
    title: "Day 2 SMS — Fast-Track Sequence",
    accent: "#0EA5E9",
    channel: "SMS",
    Icon: MessageSquare,
    from: "Alex · dedicated Twilio number",
    subject: null,
    smsBody:
      "Hi Dr. Webb, Alex here — left you a voicemail yesterday about reducing device setup complexity. Worth 90 seconds? Reply YES and I'll send a quick overview. Or grab time here: [link]",
    badge: "98% open rate channel",
  },
  "day3-email": {
    id: "day3-email",
    label: "Day 3 Email",
    title: "Day 3 Email — Fast-Track Sequence",
    accent: "#F59E0B",
    channel: "Email",
    Icon: Mail,
    from: "Alex (rep's real inbox via n8n)",
    subject: "Still worth 15 minutes, Dr. Webb?",
    body: `Hi Marcus,

I know timing isn't always perfect — but I wanted to follow up one more time because what we're building is specifically relevant to surgical teams managing device setup complexity.

Most of our clients were converting only 2% of their leads to demos before we worked together. The ones running our full system are now above 15%.

Worth 15 minutes to see if it makes sense for Regional Surgical?

Alex`,
    cta: "Book a Time →",
    active: true,
  },
  "disqualify": {
    id: "disqualify",
    label: "Disqualify Message",
    title: "Disqualification Notice",
    accent: "#7A9BB5",
    channel: "System",
    Icon: XCircle,
    from: "System · CRM",
    subject: null,
    systemBody: `Lead disqualified after 5 unanswered touches.

Actions performed:
• Tagged COLD, archived from active sequences
• Removed from send list (SMS + Email)
• Scheduled for re-score in 90 days
• Acquisition cost recorded: $186

Rep will receive a monthly digest of archived leads with re-engagement opportunities.`,
  },
};

const PREVIEW_ORDER = ["ft-day1", "day2-sms", "day3-email", "disqualify"];

const CHANNEL_COMPARE = [
  {
    id: "cc-email",
    Icon: Mail,
    label: "Email",
    accent: "#F59E0B",
    open: "34%",
    best: "longer context, booking links, case studies",
    timing: "business hours, local TZ",
  },
  {
    id: "cc-sms",
    Icon: MessageSquare,
    label: "SMS",
    accent: "#00C2A8",
    open: "98%",
    best: "quick re-engagement, booking link delivery, voicemail follow-through",
    source:
      "Blueprint Insight 4 — \"98% open rate, the most underused channel in medical device sales\"",
    highlight: true,
  },
];

/* --- Helpers ------------------------------------------------------ */

function channelColor(channel) {
  if (channel === "Email") return "#F59E0B";
  if (channel === "SMS") return "#0EA5E9";
  if (channel === "Voice") return "#00C2A8";
  return "#7A9BB5";
}

function EngagementLabel({ text, tone, complete }) {
  const palette = {
    green: "#22C55E",
    teal: "#00C2A8",
    amber: "#F59E0B",
    muted: "#7A9BB5",
    red: "#EF4444",
  };
  const color = palette[tone] || "#7A9BB5";

  if (complete) {
    return (
      <span
        className="pill pill-green"
        style={{ fontSize: 10.5 }}
      >
        <CheckCircle2 size={11} />
        {text}
      </span>
    );
  }
  return (
    <span
      style={{
        fontFamily: "Inter, sans-serif",
        fontSize: 12,
        color: color,
        fontWeight: 500,
      }}
    >
      {text}
    </span>
  );
}

/* --- Sub-components ----------------------------------------------- */

function TopStats() {
  return (
    <section data-testid="top-stats" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {TOP_STATS.map((s, i) => {
        const { Icon } = s;
        return (
          <article
            key={s.id}
            data-testid={s.id}
            className="card-glass card-interactive p-5 enter-up"
            style={{ animationDelay: `${i * 60}ms`, position: "relative" }}
          >
            {s.highlight && (
              <span
                style={{
                  position: "absolute",
                  top: 14,
                  right: 14,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "0.12em",
                  color: "#00C2A8",
                  padding: "2px 6px",
                  borderRadius: 4,
                  background: "rgba(0, 194, 168, 0.10)",
                  border: "1px solid rgba(0, 194, 168, 0.30)",
                }}
              >
                {s.highlight}
              </span>
            )}
            <div className="flex items-start justify-between mb-4">
              <div
                className="flex items-center justify-center rounded-lg"
                style={{
                  width: 36,
                  height: 36,
                  background: `${s.accent}12`,
                  border: `1px solid ${s.accent}30`,
                }}
              >
                <Icon size={16} color={s.accent} />
              </div>
            </div>
            <div
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 12,
                fontWeight: 500,
                color: "var(--text-secondary)",
                marginBottom: 8,
              }}
            >
              {s.label}
            </div>
            <div className="stat-value" data-testid={`${s.id}-value`}>
              {s.value}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {s.subs.map((sub, si) => (
                <div key={si} className="flex items-baseline gap-1.5">
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 10.5,
                      color: "var(--text-muted)",
                    }}
                  >
                    {sub.k}
                  </span>
                  {sub.v && (
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 12,
                        color: sub.c,
                        fontWeight: 600,
                      }}
                    >
                      {sub.v}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </article>
        );
      })}
    </section>
  );
}

function ChannelIcon({ channel }) {
  const c = channelColor(channel);
  const Icon = channel === "Email" ? Mail : channel === "SMS" ? MessageSquare : channel === "Voice" ? Phone : Inbox;
  return (
    <span
      className="inline-flex items-center gap-1.5"
      style={{
        padding: "3px 8px",
        borderRadius: 4,
        background: `${c}12`,
        border: `1px solid ${c}30`,
        color: c,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10.5,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
      }}
    >
      <Icon size={11} />
      {channel}
    </span>
  );
}

function ActiveSequencesMonitor() {
  return (
    <article data-testid="active-sequences-monitor" className="card-glass p-6">
      <header className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: "rgba(0, 194, 168, 0.10)", border: "1px solid rgba(0, 194, 168, 0.25)" }}
          >
            <Send size={14} color="#00C2A8" />
          </div>
          <div>
            <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              Active Sequences Monitor
            </h2>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              {SEQUENCE_ROWS.length} leads · orchestrated by Make.com
            </div>
          </div>
        </div>
        <span className="pill pill-teal">
          <span className="pill-dot pill-dot-pulse" />
          Live
        </span>
      </header>

      <div style={{ overflowX: "auto" }}>
        <table
          data-testid="sequence-table"
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 720 }}
        >
          <thead>
            <tr>
              {["Name", "Company", "Sequence", "Day", "Next Touch", "Channel", "Last Engagement"].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.12em",
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    borderBottom: "1px solid var(--border-divider)",
                    fontWeight: 500,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SEQUENCE_ROWS.map((r, i) => (
              <tr
                key={r.id}
                data-testid={r.id}
                className="enter-up"
                style={{ animationDelay: `${i * 40}ms`, transition: "background 150ms ease" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(22, 35, 51, 0.5)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)", color: "var(--text-primary)", fontWeight: 500 }}>
                  {r.name}
                </td>
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)", color: "var(--text-secondary)", fontSize: 12.5 }}>
                  {r.company}
                </td>
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)" }}>
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 11,
                      padding: "3px 8px",
                      borderRadius: 4,
                      background: `${r.seqTone}12`,
                      border: `1px solid ${r.seqTone}30`,
                      color: r.seqTone,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.sequence}
                  </span>
                </td>
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)" }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "var(--text-primary)", fontWeight: 500 }}>
                    {r.day}
                  </span>
                </td>
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)" }}>
                  <div className="flex items-center gap-1.5">
                    <Clock size={11} color={r.complete ? "#22C55E" : "var(--text-muted)"} />
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 11.5,
                        color: r.complete ? "#22C55E" : "var(--text-secondary)",
                        fontWeight: r.complete ? 600 : 400,
                      }}
                    >
                      {r.next}
                    </span>
                  </div>
                </td>
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)" }}>
                  {r.channel === "—" ? (
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--text-muted)" }}>—</span>
                  ) : (
                    <ChannelIcon channel={r.channel} />
                  )}
                </td>
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)" }}>
                  <EngagementLabel text={r.engagement} tone={r.engTone} complete={r.complete} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

function PreviewToggle({ active, onChange }) {
  return (
    <div
      data-testid="preview-toggle"
      className="flex flex-wrap gap-1.5 p-1.5 rounded-lg mb-4"
      style={{ background: "rgba(15, 25, 35, 0.6)", border: "1px solid var(--border-divider)" }}
    >
      {PREVIEW_ORDER.map((k) => {
        const p = PREVIEWS[k];
        const isActive = k === active;
        return (
          <button
            key={k}
            data-testid={`preview-tab-${k}`}
            onClick={() => onChange(k)}
            className="flex items-center gap-1.5 rounded-md"
            style={{
              padding: "7px 11px",
              background: isActive ? `${p.accent}14` : "transparent",
              border: `1px solid ${isActive ? p.accent + "40" : "transparent"}`,
              color: isActive ? "var(--text-primary)" : "var(--text-secondary)",
              fontFamily: "Inter, sans-serif",
              fontSize: 11.5,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 150ms ease",
            }}
          >
            <p.Icon size={11} color={isActive ? p.accent : "currentColor"} />
            {p.label}
            {p.active && !isActive && (
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 999,
                  background: p.accent,
                  boxShadow: `0 0 6px ${p.accent}`,
                }}
              />
            )}
            {isActive && <CheckCircle2 size={11} color={p.accent} />}
          </button>
        );
      })}
    </div>
  );
}

function MessagePreview() {
  const [active, setActive] = useState("day3-email");
  const p = PREVIEWS[active];

  return (
    <article data-testid="message-preview-panel" className="card-glass p-6">
      <header className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: "rgba(14, 165, 233, 0.10)", border: "1px solid rgba(14, 165, 233, 0.25)" }}
          >
            <BookOpen size={14} color="#0EA5E9" />
          </div>
          <div>
            <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              Message Preview Panel
            </h2>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              Live template · variables auto-injected per lead
            </div>
          </div>
        </div>
      </header>

      <PreviewToggle active={active} onChange={setActive} />

      <div
        data-testid={`preview-content-${p.id}`}
        className="rounded-lg enter-up"
        style={{
          background: "rgba(15, 25, 35, 0.5)",
          border: "1px solid var(--border-divider)",
          overflow: "hidden",
        }}
      >
        {/* Preview header */}
        <div
          className="px-4 py-3 flex items-center justify-between"
          style={{
            borderBottom: "1px solid var(--border-divider)",
            background: `linear-gradient(90deg, ${p.accent}0F, transparent)`,
          }}
        >
          <div className="flex items-center gap-2.5">
            <div
              className="flex items-center justify-center rounded-md"
              style={{
                width: 28, height: 28,
                background: `${p.accent}18`,
                border: `1px solid ${p.accent}40`,
              }}
            >
              <p.Icon size={13} color={p.accent} />
            </div>
            <div>
              <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                {p.title}
              </div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "var(--text-muted)", letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 1 }}>
                Channel · {p.channel}
              </div>
            </div>
          </div>
          {p.badge ? (
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                color: "#00C2A8",
                padding: "3px 8px",
                borderRadius: 4,
                background: "rgba(0, 194, 168, 0.10)",
                border: "1px solid rgba(0, 194, 168, 0.30)",
              }}
            >
              {p.badge}
            </span>
          ) : (
            <span
              className="pill pill-teal"
              style={{ fontSize: 10 }}
            >
              <span className="pill-dot pill-dot-pulse" />
              Active
            </span>
          )}
        </div>

        {/* Preview body */}
        <div className="p-5">
          {/* From */}
          <div className="mb-3 pb-3" style={{ borderBottom: "1px dashed rgba(30, 48, 72, 0.8)" }}>
            <div className="flex items-baseline gap-2 mb-1">
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 10,
                  color: "var(--text-muted)",
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  minWidth: 60,
                }}
              >
                From
              </span>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--text-primary)" }}>
                {p.from}
              </span>
            </div>
            {p.subject && (
              <div className="flex items-baseline gap-2">
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10,
                    color: "var(--text-muted)",
                    letterSpacing: "0.10em",
                    textTransform: "uppercase",
                    minWidth: 60,
                  }}
                >
                  Subject
                </span>
                <span
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 14,
                    color: "var(--text-primary)",
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {p.subject}
                </span>
              </div>
            )}
          </div>

          {/* Body: SMS bubble, email body, or system */}
          {p.smsBody && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: 14,
                borderBottomLeftRadius: 3,
                background: "rgba(14, 165, 233, 0.10)",
                border: "1px solid rgba(14, 165, 233, 0.25)",
                maxWidth: 440,
                fontFamily: "Inter, sans-serif",
                fontSize: 13.5,
                lineHeight: 1.55,
                color: "var(--text-primary)",
              }}
            >
              {p.smsBody}
            </div>
          )}

          {p.body && (
            <>
              <pre
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 13.5,
                  lineHeight: 1.65,
                  color: "var(--text-primary)",
                  whiteSpace: "pre-wrap",
                  margin: 0,
                }}
              >
                {p.body}
              </pre>
              {p.cta && (
                <div className="mt-4">
                  <div
                    className="inline-flex items-center gap-2 rounded-lg"
                    style={{
                      padding: "9px 16px",
                      background: `linear-gradient(135deg, ${p.accent}, ${p.accent}CC)`,
                      color: "#0F1923",
                      fontFamily: "Inter, sans-serif",
                      fontSize: 13,
                      fontWeight: 700,
                      letterSpacing: "-0.005em",
                      boxShadow: `0 0 20px ${p.accent}55`,
                    }}
                  >
                    {p.cta}
                    <ExternalLink size={12} />
                  </div>
                </div>
              )}
            </>
          )}

          {p.systemBody && (
            <pre
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                lineHeight: 1.65,
                color: "var(--text-secondary)",
                whiteSpace: "pre-wrap",
                margin: 0,
                background: "rgba(30, 48, 72, 0.35)",
                padding: 14,
                borderRadius: 8,
                border: "1px dashed rgba(122, 155, 181, 0.30)",
              }}
            >
              {p.systemBody}
            </pre>
          )}
        </div>
      </div>
    </article>
  );
}

function ChannelComparison() {
  return (
    <section data-testid="channel-comparison" className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {CHANNEL_COMPARE.map((c, i) => {
        const { Icon } = c;
        return (
          <article
            key={c.id}
            data-testid={c.id}
            className="card-glass p-6 enter-up relative overflow-hidden"
            style={{
              animationDelay: `${i * 90}ms`,
              borderLeft: `3px solid ${c.accent}`,
              background: c.highlight
                ? `linear-gradient(135deg, ${c.accent}0F 0%, ${c.accent}05 100%), var(--bg-elevated)`
                : "var(--bg-elevated)",
            }}
          >
            {c.highlight && (
              <div
                style={{
                  position: "absolute",
                  top: -30,
                  right: -20,
                  color: `${c.accent}12`,
                }}
              >
                <Quote size={140} strokeWidth={1} />
              </div>
            )}

            <div className="relative">
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="flex items-center justify-center rounded-lg"
                  style={{
                    width: 40, height: 40,
                    background: `${c.accent}14`,
                    border: `1px solid ${c.accent}30`,
                  }}
                >
                  <Icon size={18} color={c.accent} />
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 700,
                      fontSize: 20,
                      letterSpacing: "-0.02em",
                      color: "var(--text-primary)",
                    }}
                  >
                    {c.label}
                  </div>
                  <div
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 10,
                      letterSpacing: "0.12em",
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                    }}
                  >
                    Channel comparison
                  </div>
                </div>
                {c.highlight && (
                  <span
                    className="ml-auto"
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 9,
                      letterSpacing: "0.12em",
                      color: c.accent,
                      padding: "3px 7px",
                      borderRadius: 4,
                      background: `${c.accent}18`,
                      border: `1px solid ${c.accent}40`,
                    }}
                  >
                    HIGHEST-LEVERAGE
                  </span>
                )}
              </div>

              {/* Open rate */}
              <div className="flex items-baseline gap-3 mb-5">
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 600,
                    fontSize: 38,
                    color: c.accent,
                    letterSpacing: "-0.02em",
                    lineHeight: 1,
                  }}
                >
                  {c.open}
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10.5,
                    color: "var(--text-muted)",
                    letterSpacing: "0.10em",
                    textTransform: "uppercase",
                  }}
                >
                  open rate
                </div>
              </div>

              {/* Meta */}
              <div className="space-y-3">
                <div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 3 }}>
                    Best for
                  </div>
                  <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--text-primary)", lineHeight: 1.5 }}>
                    {c.best}
                  </div>
                </div>

                {c.timing && (
                  <div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 3 }}>
                      Timing
                    </div>
                    <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--text-primary)", lineHeight: 1.5 }}>
                      {c.timing}
                    </div>
                  </div>
                )}

                {c.source && (
                  <div
                    className="pt-3 flex items-start gap-2"
                    style={{ borderTop: "1px solid var(--border-divider)" }}
                  >
                    <Zap size={12} color={c.accent} style={{ marginTop: 3, flexShrink: 0 }} />
                    <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5, fontStyle: "italic" }}>
                      {c.source}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}

/* --- Page --------------------------------------------------------- */

export default function EmailSms() {
  return (
    <div data-testid="email-sms-page" className="max-w-[1400px] mx-auto space-y-8">
      {/* Header */}
      <section data-testid="email-sms-header" className="enter-up">
        <div className="flex items-start gap-4">
          <div
            style={{
              width: 4,
              alignSelf: "stretch",
              background: "linear-gradient(180deg, #F59E0B, #0EA5E9)",
              borderRadius: 4,
              minHeight: 68,
            }}
          />
          <div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.14em",
                color: "var(--accent-teal)",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Module · Tier 2 Growth Engine · Make.com
            </div>
            <h1
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 800,
                fontSize: 28,
                letterSpacing: "-0.02em",
                color: "var(--text-primary)",
                lineHeight: 1.2,
                marginBottom: 8,
              }}
            >
              Email &amp; SMS Automation
            </h1>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 14,
                lineHeight: 1.55,
                color: "var(--text-secondary)",
                maxWidth: 820,
              }}
            >
              Make.com executes every email and SMS touchpoint automatically — personalized per lead, timed to their local timezone, tracked for engagement.
            </p>
          </div>
        </div>
      </section>

      {/* Top stats */}
      <TopStats />

      {/* 2-column main */}
      <section className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-7">
          <ActiveSequencesMonitor />
        </div>
        <div className="col-span-12 xl:col-span-5">
          <MessagePreview />
        </div>
      </section>

      {/* Channel comparison */}
      <ChannelComparison />
    </div>
  );
}
